export type NotePart =
  | { kind: 'text'; text: string }
  | { kind: 'link'; text: string; href: string };

// A scheme may follow `_` or `*` (Markdown emphasis) but not a letter or digit.
// A bare `www.` only starts a link on its own, not inside an email address or
// a longer host. Brackets, braces, backticks and curly quotes end a match
// (Markdown, code, smart quotes).
const URL_PATTERN =
  /(?:(?<![A-Za-z0-9])https?:\/\/|(?<![@.A-Za-z0-9-])www\.)[^\s<>"[\]{}`“”‘’]+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?'"\]…’”*_]/;

// Sentence punctuation after a URL is not part of it; a closing parenthesis is
// only when the URL opened one (e.g. Wikipedia links).
const trimUrl = (match: string) => {
  let end = match.length;
  let opens = 0;
  let closes = 0;
  for (const char of match) {
    if (char === '(') opens += 1;
    else if (char === ')') closes += 1;
  }
  while (end > 0) {
    const last = match.charAt(end - 1);
    const isUnbalancedParen = last === ')' && opens < closes;
    if (!TRAILING_PUNCTUATION.test(last) && !isUnbalancedParen) break;
    if (last === ')') closes -= 1;
    end -= 1;
  }
  return match.slice(0, end);
};

export const toNoteHref = (url: string) =>
  url.toLowerCase().startsWith('www.') ? `https://${url}` : url;

const stripScheme = (url: string) =>
  url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '');

// A bare scheme or `www.` with no host after trimming is not a link.
const findUrls = (text: string) =>
  Array.from(text.matchAll(URL_PATTERN), (match) => ({
    index: match.index,
    url: trimUrl(match[0]),
  })).filter(({ url }) => /^[^/?#]/.test(stripScheme(url)));

/** Splits free-text note content into plain text and links, keeping order. */
export const splitNoteLinks = (text: string): NotePart[] => {
  const parts: NotePart[] = [];
  let cursor = 0;

  for (const { index, url } of findUrls(text)) {
    if (index > cursor) {
      parts.push({ kind: 'text', text: text.slice(cursor, index) });
    }
    parts.push({ kind: 'link', text: url, href: toNoteHref(url) });
    cursor = index + url.length;
  }

  if (cursor < text.length) {
    parts.push({ kind: 'text', text: text.slice(cursor) });
  }
  return parts;
};

// Scheme, `www.`, host case and a trailing slash do not make a different link.
const normalizeUrl = (url: string) => {
  const withoutScheme = stripScheme(url);
  const hostEnd = withoutScheme.search(/[/?#]/);
  const host = hostEnd === -1 ? withoutScheme : withoutScheme.slice(0, hostEnd);
  const rest = hostEnd === -1 ? '' : withoutScheme.slice(hostEnd);
  return (host.toLowerCase() + rest).replace(/\/$/, '');
};

/** URLs in the original note that the AI-polished version no longer contains. */
export const findDroppedLinks = (original: string, polished: string) => {
  const kept = new Set(findUrls(polished).map(({ url }) => normalizeUrl(url)));
  const dropped = new Map<string, string>();
  for (const { url } of findUrls(original)) {
    const key = normalizeUrl(url);
    if (!kept.has(key) && !dropped.has(key)) dropped.set(key, url);
  }
  return [...dropped.values()];
};
